import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "./Ephemeris.scss";
import InnerNav from "../components/InnerNav/InnerNav";
import Footer from "../components/Footer/Footer";
import city from "../city.json";

const Ephemeris = () => {
  const [data, setData] = useState([]);

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [location, setLocation] = useState("Kolkata");

  const [tempYear, setTempYear] = useState(year);
  const [tempMonth, setTempMonth] = useState(month);
  const [tempLocation, setTempLocation] = useState(location);

  const [suggestions, setSuggestions] = useState([]);
  const [mode, setMode] = useState("tropical"); // 🔁 TOGGLE

  /* ================= FETCH ================= */

  useEffect(() => {
    const fetchEphemeris = async () => {
      try {
        if (mode === "tropical") {
          const res = await axios.get(
            `https://core-c343.onrender.com/api/ephemeris/${year}/${month}?location=${encodeURIComponent(
              location
            )}`
          );
          setData(res.data.ephemeris || []);
        } else {
          const res = await axios.get(
            `https://core-c343.onrender.com/api/lahiri/monthly?year=${year}`
          );
          setData(res.data?.data?.[month] || []);
        }
      } catch (err) {
        console.error("Error fetching ephemeris:", err);
        setData([]);
      }
    };

    fetchEphemeris();
  }, [year, month, location, mode]);

  /* ================= HANDLERS ================= */

  const handleSubmit = () => {
    setYear(tempYear);
    setMonth(tempMonth);
    setLocation(tempLocation);
  };

  const handleChangeLocation = (e) => {
    const value = e.target.value;
    setTempLocation(value);

    if (value.length > 0) {
      const filtered = city.filter((c) =>
        c.city.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filtered.slice(0, 4));
    } else {
      setSuggestions([]);
    }
  };

  const handleSelect = (cityName) => {
    setTempLocation(cityName);
    setSuggestions([]);
  };

  /* ================= SAFE PLANET LIST ================= */

  const planetNames = useMemo(() => {
    if (!data || data.length === 0) return [];

    const firstValid = data.find(
      (row) =>
        row &&
        (mode === "tropical" ? row.positions : row.planets)
    );

    if (!firstValid) return [];

    return Object.keys(
      mode === "tropical" ? firstValid.positions : firstValid.planets
    );
  }, [data, mode]);

  /* ================= RENDER ================= */

  return (
    <div className="ephemeris-container">
      <InnerNav />

      <div className="ephemeris-content">
        <header className="ephemeris-header">
          <h2>
            {new Date(year, month - 1).toLocaleString("default", {
              month: "long",
            })}{" "}
            {year} – Online Ephemeris
          </h2>
        </header>

        {/* ===== TOGGLE ===== */}
        <div className="ephemeris-toggle">
          <button
            className={mode === "tropical" ? "active" : ""}
            onClick={() => setMode("tropical")}
          >
            Tropical (Western)
          </button>
          <button
            className={mode === "vedic" ? "active" : ""}
            onClick={() => setMode("vedic")}
          >
            Vedic (Lahiri)
          </button>
        </div>

        {/* ===== OPTIONS ===== */}
        <div className="ephemeris-options">
          <label>
            Year:
            <input
              type="number"
              value={tempYear}
              onChange={(e) => setTempYear(Number(e.target.value))}
            />
          </label>

          <label>
            Month:
            <select
              value={tempMonth}
              onChange={(e) => setTempMonth(Number(e.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </option>
              ))}
            </select>
          </label>

          <div className="inputt-with-icon">
            <span className="icon">📍</span>
            <input
              type="text"
              value={tempLocation}
              onChange={handleChangeLocation}
              placeholder="Enter location"
            />
            {suggestions.length > 0 && (
              <ul className="dropdown">
                {suggestions.map((c, idx) => (
                  <li key={idx} onClick={() => handleSelect(c.city)}>
                    {c.city}, {c.state}, {c.country}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <button className="submit-ep" onClick={handleSubmit}>
            Submit
          </button>
        </div>

        {/* ===== TABLE ===== */}
        <table className="ephemeris-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Sid.t.</th>
              {planetNames.map((planet) => (
                <th key={planet}>{planet}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                <td>{row.day ?? idx + 1}</td>
                <td>{row["Sid.t."] || row.sidereal_time || "--"}</td>

                {planetNames.map((planet) => (
                  <td key={planet}>
                    {mode === "tropical"
                      ? row.positions?.[planet] ?? "--"
                      : row.planets?.[planet] ?? "--"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Footer />
    </div>
  );
};

export default Ephemeris;
